import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, PiggyBank, Target, Award, Clock, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const SavingsAndInvestments = () => {
  const products = [
    {
      icon: PiggyBank,
      title: "High-Yield Savings",
      rate: "4.50% APY",
      description: "Earn more on your savings with our competitive rates",
      features: ["No minimum balance", "FDIC insured up to $250,000", "Free transfers"]
    },
    {
      icon: TrendingUp,
      title: "Certificates of Deposit",
      rate: "5.25% APY",
      description: "Lock in higher rates with terms from 3 months to 5 years",
      features: ["Guaranteed returns", "Flexible term options", "Early withdrawal options"]
    },
    {
      icon: Target,
      title: "Money Market Accounts",
      rate: "4.75% APY",
      description: "High interest with check-writing privileges",
      features: ["Tiered interest rates", "Check-writing access", "Debit card included"]
    },
    {
      icon: Award,
      title: "Investment Services",
      rate: "Custom",
      description: "Build wealth with our expert investment advisory",
      features: ["Portfolio management", "Retirement planning", "Tax-advantaged accounts"]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-green-600 to-green-800 text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">Savings & Investments</h1>
            <p className="text-lg sm:text-xl text-green-100 max-w-3xl mx-auto mb-8">
              Grow your wealth with our comprehensive savings and investment solutions designed for your financial future
            </p>
            <Button asChild className="bg-white text-green-600 hover:bg-green-50">
              <Link to="/contact">Get Started <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {products.map((product, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <product.icon className="w-8 h-8 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold mb-2">{product.title}</h3>
                      <div className="text-3xl font-bold text-green-600">{product.rate}</div>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-6">{product.description}</p>
                  <ul className="space-y-2">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-green-600 rounded-full mr-3" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Invest With Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">Why Invest With Morgan Guaranty Trust?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <Clock className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">25+ Years Experience</h3>
              <p className="text-gray-600">Trusted wealth management since 1998</p>
            </div>
            <div className="text-center">
              <Award className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Award-Winning Service</h3>
              <p className="text-gray-600">Recognized for excellence in customer satisfaction</p>
            </div>
            <div className="text-center">
              <Users className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Expert Advisors</h3>
              <p className="text-gray-600">Certified financial planners at your service</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Start Growing Your Wealth Today</h2>
          <p className="text-lg sm:text-xl text-green-100 mb-8">
            Open an account and start earning competitive returns on your savings
          </p>
          <Button asChild size="lg" className="bg-white text-green-600 hover:bg-green-50">
            <Link to="/user/register">Open Account</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default SavingsAndInvestments;
